// Slack channels subscribed by tenant / landlord: each feeds every project the scope
// matches (the global-data scope rule). A scope is required - an unscoped channel
// would reach every project, and a customer channel never belongs on all of them.
import { useState } from "react";

import {
  useScopeSubscribeSlack,
  useSlackChannelPicker,
  useSlackScopeSubscriptions,
  useSyncScopedSlackNow,
  type SlackStageResponse,
} from "../api/hooks/useSlack";
import { SlackSyncProgress } from "../components/SlackChannelsCard";
import { SlackPickerStatus } from "../components/SlackPickerStatus";
import styles from "../components/Slack.module.css";
import { pickerPlaceholder, syncToShow } from "../components/slackChannelNotes";
import { ScopeFields } from "./GlobalDataTargetingFields";
import { GlobalSlackScopeRow } from "./GlobalSlackScopeRow";

export const GlobalSlackChannelsCard = () => {
  const list = useSlackScopeSubscriptions();
  const [adding, setAdding] = useState(false);
  const [staged, setStaged] = useState<SlackStageResponse | null>(null);
  const { status, refresh } = useSlackChannelPicker(adding);
  const subscribe = useScopeSubscribeSlack();
  const syncNow = useSyncScopedSlackNow();
  const [channelId, setChannelId] = useState("");
  const [tenant, setTenant] = useState("");
  const [landlord, setLandlord] = useState("");

  const subs = list.data?.subscriptions ?? [];
  const shown = syncToShow(staged, list.data?.active_operation_key);
  const channels = (status.data?.channels ?? []).filter((c) => c.bot_is_member);
  const chosen = channels.find((c) => c.channel_id === channelId) ?? null;
  const canAdd = Boolean(chosen) && Boolean(tenant || landlord) && !subscribe.isLoading;

  if (list.data && !list.data.configured && !list.data.workspace) {
    return (
      <section className={styles.card}>
        <h3 className={styles.title}>Slack channels by tenant or landlord</h3>
        <p className={styles.description}>
          The Slack app is not installed in this environment, so no channel can be added yet.
        </p>
      </section>
    );
  }

  const add = () => {
    if (!chosen) return;
    if (
      chosen.is_private &&
      !window.confirm(
        `#${chosen.name} is a private channel. Everyone who can open the matching projects, and their agents, will be able to read it. Add it?`,
      )
    ) {
      return;
    }
    subscribe.mutate(
      { channelId: chosen.channel_id, tenantScope: tenant || null, landlordScope: landlord || null },
      {
        onSuccess: (resp) => {
          setAdding(false);
          setChannelId("");
          setStaged(resp);
        },
      },
    );
  };

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <h3 className={styles.title}>Slack channels by tenant or landlord</h3>
        <span className={styles.count}>{subs.length} subscribed</span>
      </header>
      <p className={styles.description}>
        A channel added here feeds every project whose tenant and landlord match its scope,
        including projects created later - the place for a customer channel such as
        #customer-amazon. Each project&apos;s agents read it for themselves.
      </p>

      {list.isError && <p className={styles.error}>Could not load the scoped channels.</p>}
      {subs.length > 0 && (
        <ul className={styles.list}>
          {subs.map((sub) => (
            <GlobalSlackScopeRow key={sub.subscription_id} sub={sub} />
          ))}
        </ul>
      )}

      {adding ? (
        <>
          <div className={styles.actions}>
            <select
              className={styles.select}
              aria-label="Slack channel to add"
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
            >
              <option value="">
                {pickerPlaceholder(channels.length, status.isLoading || refresh.isLoading)}
              </option>
              {channels.map((c) => (
                <option key={c.channel_id} value={c.channel_id}>
                  #{c.name}
                  {c.is_private ? " (private)" : ""}
                </option>
              ))}
            </select>
          </div>
          <SlackPickerStatus
            refresh={refresh}
            botMention={status.data?.workspace?.bot_mention ?? "the InRange Agents bot"}
            available={channels.length}
          />
          <ScopeFields
            subject="channel"
            tenantScope={tenant}
            landlordScope={landlord}
            onTenantScopeChange={setTenant}
            onLandlordScopeChange={setLandlord}
            requireScope
            compact
          />
          <div className={styles.actions}>
            <button type="button" className={styles.button} disabled={!canAdd} onClick={add}>
              {subscribe.isLoading ? "Adding..." : "Add"}
            </button>
            <button type="button" className={styles.secondary} onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <div className={styles.actions}>
          <button type="button" className={styles.secondary} onClick={() => setAdding(true)}>
            Add a channel
          </button>
          <button
            type="button"
            className={styles.secondary}
            disabled={syncNow.isLoading || subs.length === 0}
            onClick={() => syncNow.mutate(undefined, { onSuccess: setStaged })}
          >
            {syncNow.isLoading ? "Queueing..." : "Sync now"}
          </button>
        </div>
      )}
      {subscribe.error && (
        <p className={styles.error}>Could not add the channel. {subscribe.error.message}</p>
      )}
      {syncNow.error && <p className={styles.error}>Could not queue a sync. {syncNow.error.message}</p>}
      {shown && <SlackSyncProgress key={shown.operation_key ?? "skip"} staged={shown} />}
    </section>
  );
};
